import React, { useCallback, useEffect, useMemo, useState } from "react";
import { collection, deleteDoc, doc, getDoc, getDocs, query, where } from "firebase/firestore";
import { db } from "./firebase";
import RazorpayButton from "./RazorpayButton";
import { EmptyState, PageHeader, ToneBadge } from "./petaarUi";

export default function SavedServices({ user, onOpenProvider, selectedPet }) {
  const [favoriteRows, setFavoriteRows] = useState([]);
  const [servicesById, setServicesById] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [removingId, setRemovingId] = useState("");
  const [search, setSearch] = useState("");
  const [cityFilter, setCityFilter] = useState("");

  const fetchSaved = useCallback(async () => {
    if (!user?.uid) {
      setFavoriteRows([]);
      setServicesById({});
      return;
    }
    try {
      setLoading(true);
      setLoadError("");
      const snap = await getDocs(query(collection(db, "favorites"), where("userId", "==", user.uid)));
      const favorites = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      favorites.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setFavoriteRows(favorites);

      const uniqueServiceIds = [...new Set(favorites.map((f) => String(f.serviceId || "").trim()).filter(Boolean))];
      const servicePairs = await Promise.all(
        uniqueServiceIds.map(async (serviceId) => {
          try {
            const serviceSnap = await getDoc(doc(db, "services", serviceId));
            return [serviceId, serviceSnap.exists() ? { id: serviceSnap.id, ...serviceSnap.data() } : null];
          } catch (_) {
            return [serviceId, null];
          }
        }),
      );
      setServicesById(Object.fromEntries(servicePairs));
    } catch (err) {
      setLoadError(err.message || "Unable to load saved services.");
      setFavoriteRows([]);
      setServicesById({});
    } finally {
      setLoading(false);
    }
  }, [user?.uid]);

  useEffect(() => {
    fetchSaved();
  }, [fetchSaved]);

  const removeFavorite = async (favoriteDocId) => {
    try {
      setRemovingId(favoriteDocId);
      await deleteDoc(doc(db, "favorites", favoriteDocId));
      await fetchSaved();
    } catch (err) {
      alert(err.message || "Could not remove saved service.");
    } finally {
      setRemovingId("");
    }
  };

  const decoratedRows = useMemo(
    () =>
      favoriteRows
        .map((fav) => ({
          favoriteId: fav.id,
          serviceId: String(fav.serviceId || ""),
          service: servicesById[String(fav.serviceId || "")] || null,
        }))
        .filter((item) => {
          const service = item.service;
          if (!service) return true;
          const title = String(service.title || "").toLowerCase();
          const city = String(service.city || "");
          const searchMatch = search.trim() ? title.includes(search.trim().toLowerCase()) : true;
          const cityMatch = cityFilter ? city === cityFilter : true;
          return searchMatch && cityMatch;
        }),
    [favoriteRows, servicesById, search, cityFilter],
  );

  const cityOptions = useMemo(() => {
    const set = new Set();
    Object.values(servicesById).forEach((service) => {
      if (service?.city) set.add(service.city);
    });
    return [...set].sort();
  }, [servicesById]);

  return (
    <div className="petaar-page">
      <PageHeader
        eyebrow="Saved"
        title="Shortlisted services"
        subtitle="Keep your favorite services, vets, and providers in one place so you can book them faster later."
        actions={(
          <button className="petaar-button" onClick={fetchSaved} disabled={loading}>
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        )}
      />
      {loadError && (
        <div className="petaar-notice error" style={{ marginBottom: 16 }}>
          {loadError}
        </div>
      )}
      <div className="petaar-card" style={{ marginBottom: 20 }}>
        <div className="petaar-toolbar compact">
          <div className="petaar-form-grid" style={{ width: "100%" }}>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search saved services..."
        />
        <select value={cityFilter} onChange={(e) => setCityFilter(e.target.value)}>
          <option value="">All Cities</option>
          {cityOptions.map((city) => (
            <option key={`saved-city-${city}`} value={city}>{city}</option>
          ))}
        </select>
        <button className="petaar-button--secondary" onClick={() => { setSearch(""); setCityFilter(""); }}>Clear</button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="petaar-card">Loading saved services...</div>
      ) : decoratedRows.length === 0 ? (
        <EmptyState
          icon="🔖"
          title="Nothing saved yet"
          description="Tap save on a service or provider profile to keep it ready for later."
        />
      ) : (
        <div className="petaar-grid two">
          {decoratedRows.map((row) => {
          const service = row.service;
          if (!service) {
            return (
              <div key={`saved-missing-${row.favoriteId}`} className="petaar-card">
                <strong>Service unavailable</strong>
                <p style={{ margin: "6px 0", color: "var(--muted)" }}>
                  This saved service is no longer available on the platform.
                </p>
                <button className="petaar-button--secondary" onClick={() => removeFavorite(row.favoriteId)} disabled={removingId === row.favoriteId}>
                  {removingId === row.favoriteId ? "Removing..." : "Remove from Saved"}
                </button>
              </div>
            );
          }
          return (
            <div key={`saved-${row.favoriteId}`} className="petaar-card">
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start", marginBottom: 10 }}>
                <div>
                  <h3 style={{ margin: "0 0 6px" }}>{service.title}</h3>
                  <div className="petaar-chip-row">
                    <span className="petaar-chip">{service.category}</span>
                    <span className="petaar-chip">{service.city}</span>
                  </div>
                </div>
                <ToneBadge tone="success">Saved</ToneBadge>
              </div>
              <p style={{ margin: "4px 0" }}>Price: ₹{service.price}</p>
              <p style={{ margin: "4px 0", color: "var(--muted)" }}>Verified Provider ✓</p>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button className="petaar-button--secondary" onClick={() => onOpenProvider(service)}>View Profile</button>
                <RazorpayButton
                  service={service}
                  selectedPet={selectedPet}
                  blockedDates={Array.isArray(service?.unavailableDates) ? service.unavailableDates : []}
                  blockedSlots={Array.isArray(service?.unavailableSlots) ? service.unavailableSlots : []}
                />
                <button className="petaar-button--secondary" onClick={() => removeFavorite(row.favoriteId)} disabled={removingId === row.favoriteId}>
                  {removingId === row.favoriteId ? "Removing..." : "Remove"}
                </button>
              </div>
            </div>
          );
        })}
        </div>
      )}
    </div>
  );
}
